import React, { useState } from "react";
import { Modal } from "@mui/material";
import { XIcon } from "@heroicons/react/solid";
import { Button } from "../../components/ui";
import InlineLoader from "../../components/loader/InlineLoader";

const FundRequestApprovalModal = ({ open, onClose, request, isApproving, onApprove }) => {
  const [action, setAction] = useState("approved");
  const [approvedAmount, setApprovedAmount] = useState(request?.requested_amount || "");
  const [reason, setReason] = useState("");

  React.useEffect(() => {
    if (request) {
      setAction("approved");
      setApprovedAmount(request.requested_amount || "");
      setReason("");
    }
  }, [request, open]);

  const handleSubmit = () => {
    const payload = {
      request_id: request.request_id,
      approval_status: action,
      approved_amount: action === "approved" ? request.requested_amount : action === "rejected" ? 0 : approvedAmount,
      ...(reason.trim() && { reason: reason.trim() }),
    };
    onApprove(payload);
  };

  const isPartial = action === "partial_approval";
  const isValidPartial = isPartial && approvedAmount > 0 && approvedAmount < request?.requested_amount;
  const canSubmit = action === "approved" || action === "rejected" || isValidPartial;

  return (
    <Modal open={open} onClose={isApproving ? undefined : onClose}>
      <div className="w-full h-full md:h-auto md:w-[90%] lg:w-[60%] max-w-lg bg-white rounded-none md:rounded-2xl shadow-2xl absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 overflow-hidden border-0 md:border md:border-gray-150 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-800">Approve Fund Request</h3>
          <button
            onClick={onClose}
            disabled={isApproving}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {request && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Employee:</span>
                <span className="font-semibold text-gray-800">{request.employee_name || "-"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Requested Amount:</span>
                <span className="font-semibold text-gray-800">₹{request.requested_amount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Period:</span>
                <span className="font-semibold text-gray-800">{request.from_date} to {request.to_date}</span>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Action</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "approved", label: "Approve", color: "bg-green-600 text-white hover:bg-green-700" },
                { value: "rejected", label: "Reject", color: "bg-red-600 text-white hover:bg-red-700" },
                { value: "partial_approval", label: "Partial", color: "bg-yellow-500 text-white hover:bg-yellow-600" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAction(opt.value)}
                  className={`py-2 text-sm font-semibold rounded-xl transition-all ${action === opt.value ? opt.color : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {isPartial && (
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1">Approved Amount</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-sm font-medium">₹</span>
                <input
                  type="number"
                  value={approvedAmount}
                  onChange={(e) => setApprovedAmount(parseFloat(e.target.value) || 0)}
                  min={0.01}
                  max={request?.requested_amount - 0.01}
                  step="0.01"
                  className="pl-8 pr-4 py-2 w-full rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-sm"
                />
              </div>
              {approvedAmount <= 0 && (
                <p className="text-xs text-red-500 mt-1">Partial approved amount must be greater than 0</p>
              )}
              {approvedAmount > 0 && approvedAmount >= request?.requested_amount && (
                <p className="text-xs text-red-500 mt-1">Partial approved amount cannot be equal to or above the requested amount (₹{request?.requested_amount})</p>
              )}
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={action === "rejected" ? "Enter reason for rejection..." : "Enter reason for this decision..."}
              className="w-full min-h-[80px] border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={isApproving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || isApproving}>
              {isApproving ? (
                <>
                  <span>Processing...</span>
                  <InlineLoader className="ml-2" />
                </>
              ) : (
                action === "approved" ? "Approve" : action === "rejected" ? "Reject" : "Partial Approve"
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FundRequestApprovalModal;
